import { ComponentType, useCallback, useMemo, useState } from 'react';
import { Box, Button, Divider, Fade, Theme, Typography, alpha, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Field, useFormik, FormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAsyncCallback } from 'react-async-hook';
import { RiFilter3Line, RiShareBoxLine } from 'react-icons/ri';
import clsx from 'clsx';
import { dequal } from 'dequal';

import { SubmitFormOnChange } from 'components';
import { InlineBadge } from 'components/UI';
import { Select } from 'components/forms';
import {
  CommonFilterModalProps,
  CommonFilterValues,
  FilterTransformHelpers,
  IFilterSort,
  useProductTypeOptions,
} from 'components/filters';
import { ALL_CURRENCIES_VALUE } from 'components/filters/constants';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },

  container: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '32px',
    padding: '16px 0',

    '&.table-layout': {
      padding: '16px 0 22px',
    },
  },

  filterGroup: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },

  sortGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',

    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
    },
  },

  dealsCount: {
    marginRight: '8px',
    fontWeight: 500,
    color: alpha(theme.palette.text.secondary, 0.38),
  },

  filterDivider: {
    height: 'auto',
    alignSelf: 'stretch',
  },

  filterCountChip: {
    fontSize: '12px !important',
  },

  loadingScreen: {
    position: 'absolute',
    inset: 0,
    backgroundColor: alpha(theme.palette.background.paper, 0.55),
    zIndex: 1,
  },

  divider: {
    display: 'block',
    margin: '8px 0 24px',

    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
}));

interface ITableHeaderProps {
  sort: IFilterSort | undefined;
  onSortChange: (sortState: IFilterSort | undefined) => void;
}

interface Props<Payload extends AnyObject, Values extends CommonFilterValues> {
  components: {
    TableHeader?: ComponentType<ITableHeaderProps>;
    AllFiltersModal?: ComponentType<CommonFilterModalProps<Values>>;
  };

  helpers: FilterTransformHelpers<Payload, Values>;

  initialFilters?: Partial<Payload>;
  resetValues?: Partial<Values>;
  onFiltersChange?: (filters: Payload) => void;
  onExportDeals?: (values: Payload) => void;

  contextCurrency?: string;
  currencyOptions?: { value: string; label: string }[];

  tableLayout?: boolean;

  loading?: boolean;
  totalDeals?: number | null;
}

export const TableFilters = <Payload extends AnyObject, Values extends CommonFilterValues>(
  props: Props<Payload, Values>,
) => {
  // prettier-ignore
  const { initialFilters, onFiltersChange, onExportDeals, contextCurrency, currencyOptions, tableLayout, loading, totalDeals } = props;
  const { AllFiltersModal, TableHeader } = props.components;
  const { getPayloadFromValue, getValueFromPayload, getEmptyValue, countValues } = props.helpers;

  const classes = useStyles();
  const intl = useIntl();

  const productTypeOptions = useProductTypeOptions(intl);
  // const ratingOptions = useRatingOptions();

  const [filterModal, setFilterModal] = useState(false);

  const handleSubmit = useCallback(
    (values: Values) => onFiltersChange?.(getPayloadFromValue(values)),
    [getPayloadFromValue, onFiltersChange],
  );

  const baseValues = useMemo(
    () => ({ ...getEmptyValue(), ...props.resetValues }),
    [getEmptyValue, props.resetValues],
  );

  const initialValues = useMemo(
    () => ({ ...baseValues, ...getValueFromPayload(initialFilters ?? {}) }),
    [baseValues, getValueFromPayload, initialFilters],
  );

  const formik = useFormik<Values>({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const clearForm = () => {
    formik.resetForm({ values: baseValues });
  };

  const exportDeals = useAsyncCallback(async () => {
    await onExportDeals?.(getPayloadFromValue(formik.values));
  });

  const desktopView = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'), { noSsr: true });

  const currencyValues = formik.values.currency as string[] | undefined;
  const currencyCount = currencyValues?.length ?? 0;
  const isCurrencyActive =
    currencyValues?.length
      ? !(currencyValues.length === 1
          && (currencyValues[0] === ALL_CURRENCIES_VALUE || currencyValues[0] === contextCurrency))
      : false;

  const valuesCount = countValues(formik.values) - (isCurrencyActive ? 0 : currencyCount);
  const normalizedValues = isCurrencyActive
    ? formik.values
    : { ...formik.values, currency: baseValues.currency };
  const isEmpty = dequal(normalizedValues, baseValues);

  return (
    <>
      <Box className={classes.wrapper}>
        <FormikContext.Provider value={formik}>
          <Box
            className={clsx(classes.container, { 'table-layout': tableLayout })}
            component="form"
            onSubmit={formik.handleSubmit}
          >
            <Box className={classes.filterGroup}>
              {desktopView && (
                <>
                  <Field
                    id="productType"
                    name="productType"
                    component={Select}
                    variant="filter"
                    placeholder={intl.formatMessage({ id: 'borrower.all' })}
                    options={productTypeOptions}
                    SelectProps={{ multiple: true }}
                  />
                </>
              )}

              <SubmitFormOnChange />
            </Box>

            <Box className={classes.sortGroup}>
              {tableLayout && (
                <Typography variant="caption" className={classes.dealsCount}>
                  <FormattedMessage id="showing_n_deals" values={{ amount: totalDeals }} />
                </Typography>
              )}

              {!!AllFiltersModal && (
                <Button
                  variant="filter"
                  startIcon={<RiFilter3Line />}
                  endIcon={
                    !isEmpty && (
                      <InlineBadge label={valuesCount} className={classes.filterCountChip} />
                    )
                  }
                  className={clsx({ 'value-selected': !isEmpty })}
                  onClick={() => setFilterModal(true)}
                >
                  <FormattedMessage id="all_filters" />
                </Button>
              )}

              <Button variant="filter-contained" onClick={clearForm} disabled={isEmpty}>
                <FormattedMessage id="clear_all" />
              </Button>

              {!!onExportDeals && (
                <>
                  <Divider orientation="vertical" className={classes.filterDivider} />

                  <Button
                    variant="filter"
                    startIcon={<RiShareBoxLine />}
                    onClick={exportDeals.execute}
                    disabled={exportDeals.loading}
                  >
                    <FormattedMessage id="export_as_csv" />
                  </Button>
                </>
              )}
            </Box>

            <Fade in={loading}>
              <Box className={classes.loadingScreen} />
            </Fade>
          </Box>
        </FormikContext.Provider>

        <Divider className={classes.divider} />

        {tableLayout && TableHeader && (
          <TableHeader
            sort={formik.values.sort}
            onSortChange={(newSort) => formik.setFieldValue('sort', newSort)}
          />
        )}
      </Box>

      {!!AllFiltersModal && (
        <AllFiltersModal
          open={filterModal}
          onClose={() => setFilterModal(false)}
          withSort
          initialValues={formik.values}
          onClear={clearForm}
          contextCurrency={contextCurrency}
          currencyOptions={currencyOptions}
          onApply={(values) => {
            handleSubmit(values);
            formik.setValues(values);
          }}
        />
      )}
    </>
  );
};
